
import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { sdgsData } from "@/data/sdgs";
import { storiesData } from "@/data/stories";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { ChevronRight } from "lucide-react";

const SDGPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, language } = useLanguage();
  
  const sdg = sdgsData.find(s => s.slug === slug);
  const relatedStories = storiesData.filter(story => sdg && story.sdgIds.includes(sdg.id));
  
  // Handle image loading error
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    e.currentTarget.src = "/placeholder.svg";
    e.currentTarget.classList.add("bg-gray-100");
  };
  
  // Handle if SDG is not found
  if (!sdg) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">{language === "en" ? "SDG not found" : "لم يتم العثور على الهدف"}</h1>
          <Link to="/sdgs" className="text-palsustain-green hover:underline">
            {language === "en" ? "Back to all SDGs" : "العودة إلى جميع الأهداف"}
          </Link>
        </div>
      </div>
    );
  }
  
  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* SDG Header */}
      <header 
        className="pt-32 pb-12" 
        style={{ backgroundColor: `${sdg.color}15` }}
      >
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div 
              className="w-24 h-24 md:w-32 md:h-32 rounded-full flex items-center justify-center bg-white shadow-md"
              style={{ backgroundColor: `${sdg.color}10` }}
            >
              <img 
                src={sdg.icon || "/placeholder.svg"} 
                alt={language === "en" ? sdg.title.en : sdg.title.ar} 
                className="w-16 h-16 md:w-20 md:h-20 object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const parent = e.currentTarget.parentElement;
                  if (parent) {
                    parent.innerHTML = `<span class="text-4xl font-bold" style="color: ${sdg.color}">${sdg.id}</span>`;
                  }
                }}
              />
            </div>
            
            <div className="text-center md:text-left">
              <div className="inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-medium mb-3" style={{ backgroundColor: sdg.color, color: "white" }}>
                {language === "en" ? `Goal ${sdg.id}` : `الهدف ${sdg.id}`}
              </div>
              
              <h1 className="text-3xl md:text-4xl font-bold text-palsustain-charcoal mb-4">
                {language === "en" ? sdg.title.en : sdg.title.ar}
              </h1>
              
              <p className="text-lg text-palsustain-charcoal/80 max-w-2xl">
                {language === "en" ? sdg.description.en : sdg.description.ar}
              </p>
            </div>
          </div>
        </div>
      </header>
      
      {/* Palestinian Context */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-palsustain-green mb-6">
            {t("sdg.context.title")}
          </h2>
          
          <div className="bg-palsustain-cream/40 rounded-xl p-6 md:p-8 border border-palsustain-beige/50">
            <p className="text-lg text-palsustain-charcoal/90 leading-relaxed">
              {language === "en" ? sdg.palestinianContext.en : sdg.palestinianContext.ar}
            </p>
          </div>
        </div>
      </section>
      
      {/* Related Stories */}
      <section className="py-12 bg-palsustain-cream/30">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-palsustain-green mb-8">
            {t("sdg.stories.title")} {language === "en" ? sdg.title.en : sdg.title.ar}
          </h2>
          
          {relatedStories.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {relatedStories.map((story) => (
                <article key={story.id} className="bg-white rounded-xl shadow-md overflow-hidden flex flex-col h-full transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1">
                  <div className="relative h-48 overflow-hidden bg-gray-100">
                    <img 
                      src={story.image || "/placeholder.svg"} 
                      alt={language === "en" ? story.title.en : story.title.ar} 
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      onError={handleImageError}
                    />
                  </div>
                  <div className="p-5 flex flex-col flex-grow">
                    <div className="text-xs text-palsustain-charcoal/70 mb-2">
                      {new Date(story.date).toLocaleDateString(language === "en" ? "en-US" : "ar-EG", {
                        year: "numeric",
                        month: "long",
                        day: "numeric"
                      })}
                    </div>
                    <h3 className="text-xl font-bold mb-3">
                      {language === "en" ? story.title.en : story.title.ar}
                    </h3>
                    <p className="text-palsustain-charcoal/80 mb-4 flex-grow">
                      {language === "en" ? story.summary.en : story.summary.ar}
                    </p>
                    <Link 
                      to={`/stories/${story.slug}`} 
                      className="inline-flex items-center text-palsustain-green hover:text-palsustain-green/80 transition-colors font-medium"
                    >
                      {t("home.stories.readmore")}
                      <ChevronRight className={`ml-1 h-4 w-4 ${language === "ar" ? "transform rotate-180" : ""}`} />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl p-8 text-center">
              <p className="text-lg text-palsustain-charcoal/70">
                {language === "en" 
                  ? "Stories coming soon for this SDG. Check back later!" 
                  : "قصص قادمة قريبًا لهذا الهدف. تحقق مرة أخرى لاحقًا!"
                }
              </p>
            </div>
          )}
        </div>
      </section>
      
      {/* Related SDGs */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-palsustain-green mb-6">
            {t("sdg.related.title")}
          </h2>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {sdgsData
              .filter(s => s.id !== sdg.id)
              .slice(0, 6)
              .map((relatedSdg) => (
                <Link
                  key={relatedSdg.id}
                  to={`/sdgs/${relatedSdg.slug}`}
                  className="bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1 flex flex-col items-center p-4 text-center"
                  style={{ borderTop: `4px solid ${relatedSdg.color}` }}
                >
                  <div
                    className="w-12 h-12 mb-3 bg-contain bg-center bg-no-repeat rounded-full flex items-center justify-center"
                    style={{ 
                      backgroundColor: `${relatedSdg.color}15`,
                      color: relatedSdg.color,
                      fontWeight: 'bold'
                    }}
                  >
                    <img 
                      src={relatedSdg.icon || "/placeholder.svg"} 
                      alt={language === "en" ? relatedSdg.title.en : relatedSdg.title.ar}
                      className="w-8 h-8 object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const parent = e.currentTarget.parentElement;
                        if (parent) {
                          parent.textContent = relatedSdg.id.toString();
                        }
                      }}
                    />
                  </div>
                  <span className="text-xs font-medium text-white px-2 py-1 rounded-full mb-2" style={{ backgroundColor: relatedSdg.color }}>
                    {relatedSdg.id}
                  </span>
                  <h3 className="text-sm font-bold">
                    {language === "en" ? relatedSdg.shortTitle.en : relatedSdg.shortTitle.ar}
                  </h3>
                </Link>
              ))}
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default SDGPage;
